import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { apiClient, handleApiRequest } from '@/services/api/client'
import { extractErrorMessage } from '@/utils/errorHandler'

export function RetroDocsLandingPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''
  const attempted = useRef(false)
  const [status, setStatus] = useState<'loading' | 'ready' | 'expired' | 'invalid' | 'done'>('loading')
  const [info, setInfo] = useState<{ teacher_name: string; date: string | null; attachment_count: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!token) {
      setStatus('invalid')
      return
    }
    if (attempted.current) return
    attempted.current = true
    handleApiRequest<{ valid: boolean; expired: boolean; teacher_name: string; date: string | null; attachment_count: number }>(
      apiClient.get(`/api/v1/public/retro-docs/${encodeURIComponent(token)}`),
    ).then(r => {
      if (r.expired || !r.valid) setStatus('expired')
      else {
        setInfo(r)
        setStatus('ready')
      }
    }).catch(() => setStatus('invalid'))
  }, [token])

  const upload = async (file: File) => {
    setBusy(true)
    setError(null)
    try {
      const form = new FormData()
      form.append('file', file)
      await handleApiRequest(
        apiClient.post(`/api/v1/public/retro-docs/${encodeURIComponent(token)}`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }),
      )
      setStatus('done')
    } catch (e) {
      setError(extractErrorMessage(e, { loadError: 'Upload fehlgeschlagen' }))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-xl border shadow-sm p-6 max-w-md w-full">
        {status === 'loading' && <p className="text-sm text-gray-500">Link wird geprüft…</p>}
        {status === 'invalid' && <p className="text-sm text-red-700">Ungültiger oder fehlender Link.</p>}
        {status === 'expired' && <p className="text-sm text-amber-700">Dieser Link ist abgelaufen. Bitte die Schulleitung um einen neuen Link bitten.</p>}
        {status === 'done' && <p className="text-sm text-green-700">Dokument wurde hochgeladen. Sie können dieses Fenster schliessen.</p>}
        {status === 'ready' && (
          <>
            <h1 className="text-base font-semibold mb-1">Dokument nachreichen</h1>
            <p className="text-sm text-gray-600 mb-4">
              {info?.teacher_name}{info?.date ? ` · ${info.date}` : ''}
            </p>
            {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
            <label className="flex items-center justify-center border-2 border-dashed rounded-lg px-4 py-6 text-sm text-indigo-700 cursor-pointer">
              <input
                type="file"
                className="hidden"
                disabled={busy}
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={e => {
                  const f = e.target.files?.[0]
                  if (f) void upload(f)
                  e.target.value = ''
                }}
              />
              {busy ? 'Wird hochgeladen…' : 'Datei wählen'}
            </label>
          </>
        )}
      </div>
    </div>
  )
}
